import { useEffect, useState } from 'react';
import type { BatchTask } from '@avs/contract';
import { apiClient } from '../api/client.js';
import { showToast } from '../hooks/useToasts.js';
import { Drawer } from './Drawer.js';
import { Button } from './Button.js';
import { StatusTag } from './Tag.js';
import { Icon } from '../icons/Icon.js';
import { KIND_LABEL, KIND_ICON } from '../utils/kinds.js';
import { relativeTime } from '../utils/time.js';

type Props = {
  open: boolean;
  batchId: string | null;
  onClose: () => void;
  /** mutate 之后回调，父级刷表 */
  onMutated: () => void;
};

export function BatchDetailDrawer({ open, batchId, onClose, onMutated }: Props) {
  const [batch, setBatch] = useState<BatchTask | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!open || !batchId) { setBatch(null); return; }
    let cancelled = false;
    setLoading(true);
    apiClient.getBatch({ id: batchId })
      .then((b) => { if (!cancelled) { setBatch(b); setLoading(false); } })
      .catch((e) => { if (!cancelled) { showToast(e instanceof Error ? e.message : '加载失败', 'err'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [open, batchId]);

  async function doRetry() {
    if (!batch) return;
    setActing(true);
    try {
      const next = await apiClient.retryBatch({ id: batch.id });
      setBatch(next);
      onMutated();
      showToast('已重试', 'ok');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '重试失败', 'err');
    } finally { setActing(false); }
  }
  async function doCancel() {
    if (!batch) return;
    setActing(true);
    try {
      const next = await apiClient.cancelBatch({ id: batch.id });
      setBatch(next);
      onMutated();
      showToast('已取消', 'ok');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '取消失败', 'err');
    } finally { setActing(false); }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={
        batch ? (
          <span className="row">
            <Icon name={KIND_ICON[batch.kind]} size={20} />
            <span>{batch.name}</span>
          </span>
        ) : '任务详情'
      }
      footer={
        batch ? (
          <>
            {(batch.status === 'failed' || batch.status === 'done') && (
              <Button
                leftIcon={<Icon name="refresh" size={16} />}
                onClick={() => void doRetry()}
                disabled={acting}
              >重试</Button>
            )}
            {batch.status === 'processing' && (
              <Button
                leftIcon={<Icon name="close" size={16} />}
                onClick={() => void doCancel()}
                disabled={acting}
              >取消执行</Button>
            )}
            <Button variant="ghost" onClick={onClose}>关闭</Button>
          </>
        ) : null
      }
    >
      {loading && <div className="muted">加载中…</div>}
      {!loading && !batch && <div className="muted">未找到批次</div>}
      {batch && (
        <>
          <div className="kv-row">
            <div className="kv-key">ID</div>
            <div className="kv-val mono-sm">{batch.id}</div>
          </div>
          <div className="kv-row">
            <div className="kv-key">类型</div>
            <div className="kv-val">{KIND_LABEL[batch.kind]}</div>
          </div>
          <div className="kv-row">
            <div className="kv-key">状态</div>
            <div className="kv-val"><StatusTag status={batch.status} /></div>
          </div>
          <div className="kv-row">
            <div className="kv-key">策略</div>
            <div className="kv-val">「{batch.strategy}」</div>
          </div>
          <div className="kv-row">
            <div className="kv-key">参数</div>
            <div className="kv-val">
              {batch.params.resolution} · {batch.params.duration} · 并发 {batch.params.concurrency} · 重试 {batch.params.retries} 次
            </div>
          </div>
          <div className="kv-row">
            <div className="kv-key">Job 分布</div>
            <div className="kv-val">
              <span className="jobs-bar">
                <span className="jdot done" />完成 {batch.jobs.done}
                <span className="jdot run" style={{ marginLeft: 8 }} />进行 {batch.jobs.running}
                <span className="jdot fail" style={{ marginLeft: 8 }} />失败 {batch.jobs.failed}
              </span>
            </div>
          </div>
          <div className="kv-row">
            <div className="kv-key">输出位置</div>
            <div className="kv-val mono-sm">{batch.outputLocation || '—'}</div>
          </div>
          <div className="kv-row">
            <div className="kv-key">创建时间</div>
            <div className="kv-val">{relativeTime(batch.createdAt)}</div>
          </div>
          <div className="kv-row">
            <div className="kv-key">更新时间</div>
            <div className="kv-val">{relativeTime(batch.updatedAt)}</div>
          </div>

          <div className="block-title" style={{ marginTop: 'var(--space-l)' }}>提示词</div>
          <div className="card-soft">{batch.prompt}</div>

          {batch.outputUrl && (
            <>
              <div className="block-title" style={{ marginTop: 'var(--space-l)' }}>产物</div>
              <div className="card-soft">
                <a className="btn-link" href={batch.outputUrl} target="_blank" rel="noreferrer">
                  下载结果 <Icon name="download" size={14} />
                </a>
              </div>
            </>
          )}

          {batch.errorMessage && (
            <>
              <div className="block-title" style={{ marginTop: 'var(--space-l)' }}>错误信息</div>
              <div className="alert alert-danger" style={{ marginTop: 0 }}>
                <Icon name="warning" size={18} />
                <div className="alert-body">{batch.errorMessage}</div>
              </div>
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
